import { AlertTriangle, Calendar, Bell, TrendingDown, Calculator, RefreshCw } from "lucide-react";
import { LandingHeader, LandingFooter } from "@/components/landing";
import {
  FeatureHero,
  FeatureCTA,
  RelatedFeatures,
  Breadcrumb,
  FeatureSection,
  BenefitCard,
  ExampleBlock,
  FormulaBlock,
} from "@/components/product";
import { usePageMeta } from "@/hooks/usePageMeta";
import { JsonLd } from "@/components/seo/JsonLd";

const alerts = [
  {
    days: 7,
    level: "Attention",
    message: "Plus que 7 jours de marge",
    color: "text-[#F59E0B]",
    bg: "bg-[#F59E0B]/10",
    border: "border-[#F59E0B]/30",
  },
  {
    days: 3,
    level: "Critique",
    message: "Seulement 3 jours de marge",
    color: "text-[#EF4444]",
    bg: "bg-[#EF4444]/10",
    border: "border-[#EF4444]/30",
  },
  {
    days: 1,
    level: "Urgent",
    message: "Dernier jour rentable",
    color: "text-[#DC2626]",
    bg: "bg-[#DC2626]/10",
    border: "border-[#DC2626]/30",
  },
];

const benefits = [
  {
    icon: Calendar,
    title: "Date précise calculée",
    description: "Connaissez le jour exact où votre chantier bascule dans le rouge.",
    gradient: "from-[#EF4444] to-[#DC2626]",
  },
  {
    icon: Bell,
    title: "Alertes automatiques",
    description: "Recevez des notifications à J-7, J-3 et J-1 pour réagir à temps.",
    gradient: "from-[#F59E0B] to-[#D97706]",
  },
  {
    icon: RefreshCw,
    title: "Mise à jour en temps réel",
    description: "Le Jour Critique se recalcule à chaque changement dans votre chantier.",
    gradient: "from-[#3B82F6] to-[#2563EB]",
  },
  {
    icon: TrendingDown,
    title: "Anticipation des pertes",
    description: "Prenez les bonnes décisions avant qu'il ne soit trop tard.",
    gradient: "from-[#10B981] to-[#059669]",
  },
];

const JourCritiqueFeature = () => {
  usePageMeta({ title: "Jour critique chantier — Anticipez les pertes | B8ild", description: "Calculez le jour critique de vos chantiers : le moment exact où un retard commence à vous coûter de l'argent. Fonctionnalité unique B8ild.", url: "https://b8ild.fr/product/jour-critique" });
  return (
    <div className="min-h-screen bg-[#0F172A]">
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "Jour critique chantier — Anticipez les pertes | B8ild",
        description: "Calculez le jour critique de vos chantiers : le moment exact où un retard commence à vous coûter de l'argent. Fonctionnalité unique B8ild.",
        url: "https://b8ild.fr/product/jour-critique",
        isPartOf: { "@type": "WebSite", name: "B8ild", url: "https://b8ild.fr" },
      }} />
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Accueil", item: "https://b8ild.fr/" },
          { "@type": "ListItem", position: 2, name: "Produit", item: "https://b8ild.fr/product" },
          { "@type": "ListItem", position: 3, name: "Jour Critique", item: "https://b8ild.fr/product/jour-critique" },
        ],
      }} />
      <LandingHeader />

      {/* Breadcrumb */}
      <div className="container mx-auto px-4 pt-24">
        <Breadcrumb
          items={[
            { label: "Produit", href: "/product" },
            { label: "Jour Critique" },
          ]}
        />
      </div>

      {/* Hero */}
      <FeatureHero
        badge="Jour Critique"
        title="Savez-vous quand votre"
        titleHighlight="chantier devient déficitaire ?"
        subtitle="B8ild calcule le jour exact où votre marge atteindra zéro si rien ne change. Anticipez, réagissez, préservez vos profits."
        icon={AlertTriangle}
        gradient="from-[#EF4444] to-[#DC2626]"
      />

      {/* Concept explained */}
      <FeatureSection variant="dark">
        <div className="max-w-3xl mx-auto">
          <ExampleBlock
            title="Le concept"
            content="Votre chantier a encore 12 jours de marge. Au-delà, chaque jour travaillé vous coûte de l'argent. Le Jour Critique, c'est cette date limite — et B8ild vous la calcule automatiquement."
            highlight="Jour Critique = Date à laquelle votre marge atteint 0€"
          />
        </div>
      </FeatureSection>

      {/* How it's calculated */}
      <FeatureSection
        title="Comment c'est calculé ?"
        subtitle="Une formule simple basée sur vos données réelles"
      >
        <div className="max-w-3xl mx-auto space-y-8">
          <FormulaBlock
            formula="Jours de marge = Budget restant ÷ Coût journalier équipe"
            description="Le calcul se met à jour automatiquement à chaque modification"
          />

          <div className="grid md:grid-cols-3 gap-4">
            <div className="rounded-xl bg-white/[0.02] border border-white/[0.05] p-4 text-center">
              <p className="text-xs text-white/40 uppercase tracking-wider mb-2">Budget restant</p>
              <p className="text-2xl font-bold text-white">8 400 €</p>
            </div>
            <div className="rounded-xl bg-white/[0.02] border border-white/[0.05] p-4 text-center">
              <p className="text-xs text-white/40 uppercase tracking-wider mb-2">Coût / jour</p>
              <p className="text-2xl font-bold text-white">700 €</p>
            </div>
            <div className="rounded-xl bg-gradient-to-br from-[#EF4444]/10 to-transparent border border-[#EF4444]/20 p-4 text-center">
              <p className="text-xs text-[#EF4444]/70 uppercase tracking-wider mb-2">Jour Critique</p>
              <p className="text-2xl font-bold text-[#EF4444]">J+12</p>
            </div>
          </div>
        </div>
      </FeatureSection>

      {/* Alerts system */}
      <FeatureSection variant="dark" title="Système d'alertes progressives">
        <div className="max-w-2xl mx-auto">
          <div className="space-y-4">
            {alerts.map((alert, index) => (
              <div
                key={index}
                className={`rounded-xl ${alert.bg} border ${alert.border} p-4 flex items-center gap-4`}
              >
                <div className={`h-12 w-12 rounded-xl ${alert.bg} flex items-center justify-center`}>
                  <Bell className={`h-6 w-6 ${alert.color}`} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-semibold ${alert.color}`}>
                      Alerte J-{alert.days}
                    </span>
                    <span className="text-xs text-white/40">•</span>
                    <span className="text-xs text-white/40">{alert.level}</span>
                  </div>
                  <p className="text-white/70 mt-1">{alert.message}</p>
                </div>
                <div className={`text-2xl font-bold ${alert.color}`}>
                  J-{alert.days}
                </div>
              </div>
            ))}
          </div>
        </div>
      </FeatureSection>

      {/* Benefits */}
      <FeatureSection
        title="Pourquoi c'est essentiel"
        subtitle="Gardez le contrôle sur vos chantiers"
      >
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
          {benefits.map((benefit, index) => (
            <BenefitCard key={index} {...benefit} />
          ))}
        </div>
      </FeatureSection>

      {/* Visual Preview */}
      <FeatureSection variant="dark" title="Visualisation du Jour Critique">
        <div className="max-w-3xl mx-auto">
          <div className="rounded-2xl p-[1px] bg-gradient-to-b from-white/[0.15] to-white/[0.02]">
            <div className="rounded-2xl bg-[#0F0F14]/90 backdrop-blur-xl p-6">
              {/* Timeline visualization */}
              <div className="mb-6">
                <div className="flex justify-between text-sm text-white/50 mb-2">
                  <span>Début du chantier</span>
                  <span>Aujourd'hui</span>
                  <span className="text-[#EF4444]">Jour Critique</span>
                </div>
                <div className="relative h-4 bg-white/[0.05] rounded-full overflow-hidden">
                  {/* Progress bar */}
                  <div className="absolute left-0 top-0 h-full w-[60%] bg-gradient-to-r from-[#10B981] via-[#F59E0B] to-[#F59E0B] rounded-full" />
                  {/* Today marker */}
                  <div className="absolute top-0 h-full w-1 bg-white left-[60%] -translate-x-1/2" />
                  {/* Critical day marker */}
                  <div className="absolute top-0 h-full w-1 bg-[#EF4444] left-[85%] -translate-x-1/2" />
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 mt-8">
                <div className="text-center">
                  <p className="text-3xl font-bold text-white">18</p>
                  <p className="text-sm text-white/40">jours écoulés</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-[#F59E0B]">12</p>
                  <p className="text-sm text-white/40">jours de marge</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-[#EF4444]">15 fév</p>
                  <p className="text-sm text-white/40">jour critique</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </FeatureSection>

      {/* Pourquoi c'est important */}
      <section className="py-16 bg-[#0A0A0F]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-6">
              Le jour où votre chantier bascule dans le rouge
            </h2>
            <div className="space-y-4 text-white/60 leading-relaxed">
              <p>
                Un chantier de renovation a 45 000 EUR HT. Le devis prevoit 35 jours de travaux, 4 ouvriers. Au debut tout va bien. Mais au 20e jour, un fournisseur de menuiseries a 8 jours de retard. Les ouvriers sont la, payes, mais attendent les fenetres. Le cout main d'oeuvre continue de tourner : 720 EUR/jour pour rien.
              </p>
              <p>
                Sans indicateur, vous ne le voyez pas. Vous pensez que "ca se rattrape". En realite, le jour critique — le jour ou votre marge passe sous zero — etait au 28e jour. Vous l'avez depasse au 32e jour sans le savoir. Resultat : 2 800 EUR de perte nette au lieu de 6 500 EUR de benefice prevu.
              </p>
              <p>
                B8ild calcule ce jour critique en temps reel. 7 jours avant, 3 jours avant, 1 jour avant : alerte. Vous avez le temps de reagir.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Questions frequentes */}
      <section className="py-16 bg-[#08080C]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-8">
              Questions frequentes
            </h2>
            <div className="space-y-6">
              {[
                {
                  q: "C'est quoi le jour critique d'un chantier ?",
                  a: "Le jour exact ou vos couts cumules depassent votre budget. Passe cette date, chaque journee supplementaire cree du deficit. Sur un chantier a 30 000 EUR avec 18% de marge previsionnelle, le jour critique tombe souvent autour du 80e-85e% d'avancement. Apres, chaque jour de retard coute entre 150 EUR et 500 EUR.",
                },
                {
                  q: "Comment anticiper un depassement de budget ?",
                  a: "Suivez 3 indicateurs chaque semaine : le % d'avancement reel vs le % de budget consomme, le cout main d'oeuvre reel vs prevu, et les achats materiaux reels vs devis. Si le budget consomme avance plus vite que l'avancement physique, vous avez un probleme. B8ild fait ce suivi automatiquement.",
                },
                {
                  q: "A quel moment faut-il s'inquieter sur un chantier ?",
                  a: "Des que les couts reels depassent 50% du budget alors que l'avancement physique est sous 45%. Autre signal : quand le cout main d'oeuvre depasse le previsionnel de plus de 10% a mi-parcours. A ce stade, il reste de la marge pour corriger. Attendre la fin, c'est trop tard.",
                },
              ].map((faq, i) => (
                <div key={i} className="border-b border-white/[0.06] pb-6">
                  <h3 className="text-lg font-medium text-white mb-3">{faq.q}</h3>
                  <p className="text-white/50 leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <FeatureCTA
        title="Anticipez le Jour Critique"
        subtitle="Ne laissez plus un chantier basculer dans le rouge sans être prévenu."
      />

      {/* Related Features */}
      <RelatedFeatures currentFeatureId="jour-critique" />

      <LandingFooter />
    </div>
  );
};

export default JourCritiqueFeature;
